import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="31015" className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-mono text-xs font-semibold uppercase tracking-widest text-foreground/80 transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavigation({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-2xl pt-24 px-6 lg:hidden animate-in fade-in slide-in-from-top-5 duration-200">
      <nav className="flex flex-col gap-6 items-center">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-mono text-xl font-bold uppercase tracking-widest text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
            {item.label}
          </Link>)}
        <Button variant="default" className="mt-8 text-sm font-bold uppercase tracking-widest w-full max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" asChild>
          <Link to="https://app.cncwise.com/signup" onClick={() => setIsOpen(false)}>
            Get Started
          </Link>
        </Button>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <MenuProvider menu_id="30">
      <header id="site-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-500 text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/10 shadow-md" : "bg-background border-b border-transparent")}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 z-50">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/CNCWise Logo PNG.png" alt="Logo" className="h-8 w-auto" />
          </Link>

          <div className="hidden lg:flex flex-1 justify-center">
            <DesktopNavigation />
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <Button variant="default" className="bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs px-6 py-2 hover:bg-primary/90 hover:shadow-[0_0_20px_-2px_var(--color-primary)] transition-all duration-300 rounded-sm" asChild>
              <Link to="https://app.cncwise.com/signup">Get Started</Link>
            </Button>
            <Button variant="default" className="bg-[#22c55e] text-black font-bold uppercase tracking-widest text-xs px-6 py-2 hover:bg-[#22c55e]/90 transition-all duration-300 rounded-sm" asChild>
              <Link to="https://app.cncwise.com/login">Sign In</Link>
            </Button>
          </div>

          <Button variant="ghost" size="icon" className="lg:hidden z-50 text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>

          <MobileNavigation isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </div>
      </header>
    </MenuProvider>;
}